import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Clock, FileText, Sparkles } from 'lucide-react';
import { DocumentTypeLabel, StatusBadge } from '@/components/app/status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { cn, formatAmount, formatDateISO } from '@/lib/utils';

export const Route = createFileRoute('/')({
	component: Dashboard,
});

function Dashboard() {
	const navigate = useNavigate();
	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });
	const recentQuery = useQuery({
		queryKey: ['documents', { limit: 10 }],
		queryFn: () => api.documents({ limit: 10 }),
	});
	const reviewQuery = useQuery({
		queryKey: ['documents', { needs_review: true }],
		queryFn: () => api.documents({ needs_review: true, limit: 1 }),
	});
	const errorQuery = useQuery({
		queryKey: ['documents', { status: 'error' }],
		queryFn: () => api.documents({ status: 'error', limit: 1 }),
	});
	const completedQuery = useQuery({
		queryKey: ['documents', { status: 'completed' }],
		queryFn: () => api.documents({ status: 'completed', limit: 1 }),
	});
	const processingQuery = useQuery({
		queryKey: ['documents', { status: 'processing' }],
		queryFn: () => api.documents({ status: 'processing', limit: 1 }),
	});

	const totalCount =
		(recentQuery.data?.count ?? 0);

	return (
		<div className="mx-auto max-w-screen-2xl space-y-6">
			<div className="flex flex-col gap-1">
				<h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
				<p className="text-sm text-muted-foreground">
					Overzicht van alle documenten per entiteit, met accenten op dingen die aandacht vragen.
				</p>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<StatCard
					icon={<FileText className="h-4 w-4" />}
					label="Totaal"
					value={totalCount}
					description="Documenten geïndexeerd"
					tone="neutral"
				/>
				<StatCard
					icon={<AlertCircle className="h-4 w-4" />}
					label="Review nodig"
					value={reviewQuery.data?.count ?? 0}
					description="Classifier onzeker (conf < 0.70)"
					tone="warning"
					onClick={() => navigate({ to: '/documents', search: { needs_review: 'true' } })}
				/>
				<StatCard
					icon={<Clock className="h-4 w-4" />}
					label="Fout / retry"
					value={errorQuery.data?.count ?? 0}
					description="Documenten met foutstatus"
					tone="destructive"
					onClick={() => navigate({ to: '/documents', search: { status: 'error' } })}
				/>
				<StatCard
					icon={<CheckCircle2 className="h-4 w-4" />}
					label="Volledig verwerkt"
					value={completedQuery.data?.count ?? 0}
					description="Status completed"
					tone="success"
					onClick={() => navigate({ to: '/documents', search: { status: 'completed' } })}
				/>
			</div>

			<div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
				<Card className="xl:col-span-1">
					<CardHeader>
						<CardTitle>Entiteiten</CardTitle>
						<CardDescription>Intake-adressen per entiteit</CardDescription>
					</CardHeader>
					<CardContent className="space-y-2">
						{entitiesQuery.data?.entities.map((e) => (
							<Link
								key={e.id}
								to="/documents"
								search={{ entity_code: e.code }}
								className="flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors hover:bg-muted"
							>
								<div className="flex flex-col">
									<span className="font-medium">{e.name}</span>
									<span className="font-mono text-xs text-muted-foreground">{e.code}@in.docr.nl</span>
								</div>
								<span className="text-xs text-muted-foreground">Bekijk</span>
							</Link>
						))}
						{!entitiesQuery.data && (
							<div className="text-sm text-muted-foreground">Laden...</div>
						)}
					</CardContent>
				</Card>

				<Card className="xl:col-span-2">
					<CardHeader className="flex flex-row items-center justify-between space-y-0">
						<div>
							<CardTitle>Recente documenten</CardTitle>
							<CardDescription>Laatste 10 binnengekomen documenten</CardDescription>
						</div>
						<Button asChild size="sm" variant="outline">
							<Link to="/documents">Alle documenten</Link>
						</Button>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Datum</TableHead>
									<TableHead>Aan</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Tegenpartij</TableHead>
									<TableHead className="text-right">Bedrag</TableHead>
									<TableHead>Status</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{recentQuery.data?.items.map((doc) => (
									<TableRow
										key={doc.id}
										className="cursor-pointer"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										<TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
											{formatDateISO(doc.document_date ?? doc.created_at)}
										</TableCell>
										<TableCell className="whitespace-nowrap">
											<Badge variant="muted">{doc.entity_code ?? '—'}</Badge>
										</TableCell>
										<TableCell>
											<DocumentTypeLabel type={doc.document_type} />
											{doc.needs_review ? (
												<div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
													<Sparkles className="h-3 w-3" /> review
												</div>
											) : null}
										</TableCell>
										<TableCell className="max-w-[18rem] truncate">
											{doc.counterparty_name ?? <span className="text-muted-foreground">—</span>}
										</TableCell>
										<TableCell className="whitespace-nowrap text-right font-mono text-xs">
											{formatAmount(doc.amount_total, doc.currency ?? 'EUR')}
										</TableCell>
										<TableCell>
											<StatusBadge status={doc.status} />
										</TableCell>
									</TableRow>
								))}
								{recentQuery.data && recentQuery.data.items.length === 0 && (
									<TableRow>
										<TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
											Nog geen documenten binnengekomen. Stuur een mail naar een intake-adres.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</div>

			{processingQuery.data && processingQuery.data.count > 0 && (
				<Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30">
					<CardContent className="flex items-center gap-3 p-4">
						<Clock className="h-4 w-4 text-blue-700 dark:text-blue-300" />
						<span className="text-sm">
							<span className="font-medium">{processingQuery.data.count}</span> document
							{processingQuery.data.count === 1 ? '' : 'en'} in verwerking.
						</span>
					</CardContent>
				</Card>
			)}
		</div>
	);
}

interface StatCardProps {
	icon: React.ReactNode;
	label: string;
	value: number;
	description: string;
	tone: 'neutral' | 'warning' | 'destructive' | 'success';
	// Programmatisch navigeren via useNavigate; TanStack Router-Link met
	// search-prop gaf een rendering-race waardoor de filter pas na refresh
	// zichtbaar werd in de lijst.
	onClick?: () => void;
}

function StatCard({ icon, label, value, description, tone, onClick }: StatCardProps) {
	const toneClass = {
		neutral: 'text-foreground',
		warning: 'text-amber-700 dark:text-amber-300',
		destructive: 'text-red-700 dark:text-red-300',
		success: 'text-emerald-700 dark:text-emerald-300',
	}[tone];

	const content = (
		<Card className="transition-colors hover:bg-muted/30">
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
				<span className={cn('rounded-md bg-muted p-1.5', toneClass)}>{icon}</span>
			</CardHeader>
			<CardContent>
				<div className={cn('text-3xl font-semibold tracking-tight', toneClass)}>{value}</div>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);

	if (onClick) {
		return (
			<button type="button" onClick={onClick} className="block w-full text-left">
				{content}
			</button>
		);
	}
	return content;
}
